import { MyTaskExecutionSection } from "shared/api";
import { MyTaskExecutionRow } from "shared/types";

/** Total subsections anywhere under a section (recursive). */
const countDescendantSections = (section: MyTaskExecutionSection): number =>
  (section.subsections ?? []).length +
  (section.subsections ?? []).reduce(
    (sum, sub) => sum + countDescendantSections(sub),
    0
  );

/**
 * Convert the recursive `get_my_task_executions` payload into antd tree rows.
 * At every level subsections (expandable section rows) are listed before the
 * section's own task executions (leaf rows) — mirrors the Task Hub tree.
 */
export const buildMyTaskExecutionRows = (
  node: MyTaskExecutionSection
): MyTaskExecutionRow[] => {
  const sectionRows: MyTaskExecutionRow[] = (node.subsections ?? []).map(
    (sub) => ({
      key: `section-${sub.id}`,
      rowType: "section",
      name: sub.name ?? "—",
      total: sub.total_task_executions,
      sectionCount: countDescendantSections(sub),
      children: buildMyTaskExecutionRows(sub),
    })
  );

  const taskRows: MyTaskExecutionRow[] = (node.task_executions ?? []).map(
    (te) => ({
      key: `task-${te.id}`,
      rowType: "task",
      id: te.id,
      name: te.task?.name ?? "—",
      icon: te.task?.icon,
      expected: te.timestamp_expected,
      executed: te.timestamp_executed,
      message: te.message,
      priority: te.priority,
      status: te.status,
      daysOverdue: te.calc_days_overdue,
    })
  );

  return [...sectionRows, ...taskRows];
};

/** Collect the row keys of every section row (used to expand the whole tree). */
export const collectMyTaskExecutionSectionKeys = (
  rows: MyTaskExecutionRow[]
): string[] => {
  const acc: string[] = [];
  const walk = (items: MyTaskExecutionRow[]) => {
    items.forEach((row) => {
      if (row.rowType === "section") {
        acc.push(row.key);
        walk(row.children ?? []);
      }
    });
  };
  walk(rows);
  return acc;
};
